import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { search as searchVehicules } from "../database/vehiculeService";
import { loadFerrailleurs, loadGroupes } from "../reseau/reseauService";
import { deleteEnchere, loadEncheres, saveEnchere } from "./enchereService";

const STATUS_OPTIONS = [
  { value: "PROGRAMMEE", label: "Programmer" },
  { value: "A_VENIR", label: "A venir" },
  { value: "EN_COURS", label: "En cours" },
  { value: "CLOTUREE", label: "Clotures" },
];

const CIBLAGE_OPTIONS = ["TOUS", "A", "B", "C"];

const emptyForm = {
  titre: "",
  vehiculeId: "",
  status: "PROGRAMMEE",
  ciblage: "TOUS",
  dateDebut: "",
  dateFin: "",
  prixDepart: "",
  pasMinimal: "100",
  antiSniping: true,
  groupeIds: [],
  ferrailleurIds: [],
  conditions: "",
};

const statusColor = {
  PROGRAMMEE: "info",
  A_VENIR: "primary",
  EN_COURS: "success",
  CLOTUREE: "secondary",
};

const labelOfStatus = (status) => STATUS_OPTIONS.find((item) => item.value === status)?.label || status || "-";

const asNumberOrNull = (value) => (value === "" || value === null || value === undefined ? null : Number(value));
const firstVehiclePhoto = (enchere) => (enchere?.vehiculePhotos || []).find((photo) => photo?.path)?.path;

function EncheresAdminPage() {
  const [encheres, setEncheres] = useState([]);
  const [vehicules, setVehicules] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [ferrailleurs, setFerrailleurs] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    loadPageData();
  }, []);

  useEffect(() => {
    loadList(statusFilter);
  }, [statusFilter]);

  const loadPageData = async () => {
    setLoading(true);
    try {
      const [vehiculeResponse, groupeResponse, ferrailleurResponse] = await Promise.all([
        searchVehicules({ criteria: {}, page: 0, size: 200 }),
        loadGroupes(),
        loadFerrailleurs(),
      ]);
      setVehicules(vehiculeResponse?.data?.body?.content || []);
      setGroupes(groupeResponse?.data?.body || []);
      setFerrailleurs(ferrailleurResponse?.data?.body || []);
      await loadList("");
    } finally {
      setLoading(false);
    }
  };

  const loadList = async (status) => {
    const response = await loadEncheres(status);
    setEncheres(response?.data?.body || []);
  };

  const setField = (field, value) => {
    setForm((prev) => {
      if (field !== "ciblage") {
        return { ...prev, [field]: value };
      }

      const allowedGroupIds = groupes
        .filter((groupe) => value === "TOUS" || groupe.categorie === value)
        .map((groupe) => groupe.id);

      return {
        ...prev,
        ciblage: value,
        groupeIds: (prev.groupeIds || []).filter((id) => allowedGroupIds.includes(id)),
      };
    });
  };

  const toggleId = (field, id) => {
    setForm((prev) => {
      const current = prev[field] || [];
      return {
        ...prev,
        [field]: current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
      };
    });
  };

  const resetForm = () => {
    setForm(emptyForm);
  };

  const editEnchere = (enchere) => {
    setForm({
      ...emptyForm,
      ...enchere,
      vehiculeId: enchere.vehiculeId || "",
      prixDepart: enchere.prixDepart || "",
      pasMinimal: enchere.pasMinimal || "100",
      dateDebut: enchere.dateDebut || "",
      dateFin: enchere.dateFin || "",
      groupeIds: enchere.groupeIds || [],
      ferrailleurIds: enchere.ferrailleurIds || [],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await saveEnchere({
        ...form,
        vehiculeId: asNumberOrNull(form.vehiculeId),
        prixDepart: asNumberOrNull(form.prixDepart),
        pasMinimal: asNumberOrNull(form.pasMinimal),
      });
      resetForm();
      await loadList(statusFilter);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await deleteEnchere(deleteTarget.id);
    setDeleteTarget(null);
    await loadList(statusFilter);
  };

  const stats = useMemo(() => {
    return STATUS_OPTIONS.reduce((acc, item) => {
      acc[item.value] = encheres.filter((enchere) => enchere.status === item.value).length;
      return acc;
    }, {});
  }, [encheres]);

  const groupesByCiblage = useMemo(() => {
    return groupes.filter((groupe) => form.ciblage === "TOUS" || groupe.categorie === form.ciblage);
  }, [groupes, form.ciblage]);

  return (
    <div className="page-content auction-page">
      <Container fluid>
        <Breadcrumbs title="Encheres" breadcrumbItems={[{ title: "Modules", link: "#" }, { title: "Encheres", link: "#" }]} />

        <Row className="g-4">
          <Col xl={4}>
            <Card className="h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h4 className="mb-1">Créer une enchère</h4>
                    <p className="text-muted mb-0">Programme, cible et publie une vente.</p>
                  </div>
                  {form.id && <Badge color="info">Edition</Badge>}
                </div>

                <form onSubmit={submit}>
                  <Row className="g-3">
                    <Col xs={12}>
                      <Label>Titre</Label>
                      <Input value={form.titre} onChange={(event) => setField("titre", event.target.value)} placeholder="Ex: Lot Dacia sinistree" required />
                    </Col>
                    <Col xs={12}>
                      <Label>Vehicule</Label>
                      <Input type="select" value={form.vehiculeId} onChange={(event) => setField("vehiculeId", event.target.value)}>
                        <option value="">Aucun vehicule associe</option>
                        {vehicules.map((vehicle) => (
                          <option key={vehicle.id} value={vehicle.id}>
                            {[vehicle.marque, vehicle.modele, vehicle.numChassis].filter(Boolean).join(" - ") || `Vehicule #${vehicle.id}`}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={6}>
                      <Label>Status</Label>
                      <Input type="select" value={form.status} onChange={(event) => setField("status", event.target.value)}>
                        {STATUS_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                      </Input>
                    </Col>
                    <Col md={6}>
                      <Label>Ciblage</Label>
                      <Input type="select" value={form.ciblage} onChange={(event) => setField("ciblage", event.target.value)}>
                        {CIBLAGE_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
                      </Input>
                    </Col>
                    <Col md={6}>
                      <Label>Date debut</Label>
                      <Input type="datetime-local" value={form.dateDebut || ""} onChange={(event) => setField("dateDebut", event.target.value)} />
                    </Col>
                    <Col md={6}>
                      <Label>Date fin</Label>
                      <Input type="datetime-local" value={form.dateFin || ""} onChange={(event) => setField("dateFin", event.target.value)} />
                    </Col>
                    <Col md={6}>
                      <Label>Prix depart</Label>
                      <Input type="number" value={form.prixDepart} onChange={(event) => setField("prixDepart", event.target.value)} min="0" />
                    </Col>
                    <Col md={6}>
                      <Label>Pas minimal</Label>
                      <Input type="number" value={form.pasMinimal} onChange={(event) => setField("pasMinimal", event.target.value)} min="0" />
                    </Col>
                    <Col xs={12}>
                      <Label>Groupes ciblés</Label>
                      <div className="choice-grid">
                        {groupesByCiblage.map((groupe) => (
                          <button type="button" key={groupe.id} className={form.groupeIds.includes(groupe.id) ? "choice-pill active" : "choice-pill"} onClick={() => toggleId("groupeIds", groupe.id)}>
                            {groupe.nom} <span>{groupe.categorie}</span>
                          </button>
                        ))}
                      </div>
                      {!groupesByCiblage.length && (
                        <div className="text-muted small mt-2">Aucun groupe associe a ce ciblage.</div>
                      )}
                    </Col>
                    <Col xs={12}>
                      <Label>Férailleurs spécifiques</Label>
                      <div className="choice-grid">
                        {ferrailleurs.map((ferrailleur) => (
                          <button type="button" key={ferrailleur.id} className={form.ferrailleurIds.includes(ferrailleur.id) ? "choice-pill active" : "choice-pill"} onClick={() => toggleId("ferrailleurIds", ferrailleur.id)}>
                            {ferrailleur.nom}
                          </button>
                        ))}
                      </div>
                    </Col>
                    <Col xs={12}>
                      <Label>Conditions</Label>
                      <Input type="textarea" rows="3" value={form.conditions || ""} onChange={(event) => setField("conditions", event.target.value)} />
                    </Col>
                    <Col xs={12}>
                      <div className="d-flex gap-2">
                        <Button color="primary" type="submit" disabled={saving}>
                          {saving && <Spinner size="sm" className="me-2" />}
                          {form.id ? "Mettre a jour" : "Créer"}
                        </Button>
                        <Button color="light" type="button" onClick={resetForm}>Nouveau</Button>
                      </div>
                    </Col>
                  </Row>
                </form>
              </CardBody>
            </Card>
          </Col>

          <Col xl={8}>
            <Card className="mb-4">
              <CardBody>
                <Row className="g-3 align-items-end">
                  <Col lg={5}>
                    <h4 className="mb-1">Pilotage des enchères</h4>
                    <p className="text-muted mb-0">Suivi par statut et ciblage.</p>
                  </Col>
                  <Col lg={4}>
                    <Label>Filtrer par statut</Label>
                    <Input type="select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                      <option value="">Tous</option>
                      {STATUS_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                    </Input>
                  </Col>
                  <Col lg={3}>
                    <div className="auction-mini-stats">
                      {STATUS_OPTIONS.map((item) => <span key={item.value}>{item.label}: <b>{stats[item.value] || 0}</b></span>)}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : (
              <Row className="g-3">
                {encheres.map((enchere) => (
                  <Col md={6} key={enchere.id}>
                    <Card className="h-100 auction-card">
                      <CardBody>
                        {firstVehiclePhoto(enchere) && (
                          <img className="auction-vehicle-photo mb-3" src={firstVehiclePhoto(enchere)} alt={enchere.vehiculeLabel || enchere.titre || "Vehicule"} />
                        )}
                        <div className="d-flex justify-content-between gap-3 mb-3">
                          <div>
                            <h5 className="mb-1">{enchere.titre || enchere.vehiculeLabel || "Enchere"}</h5>
                            <div className="text-muted small">{enchere.vehiculeLabel || "Sans vehicule"}</div>
                          </div>
                          <Badge color={statusColor[enchere.status] || "secondary"}>{labelOfStatus(enchere.status)}</Badge>
                        </div>
                        <Row className="g-3 mb-3">
                          <Col xs={6}><span className="text-muted small">Ciblage</span><strong>{enchere.ciblage || "-"}</strong></Col>
                          <Col xs={6}><span className="text-muted small">Prix depart</span><strong>{enchere.prixDepart || "-"} MAD</strong></Col>
                          <Col xs={6}><span className="text-muted small">Debut</span><strong>{enchere.dateDebut || "-"}</strong></Col>
                          <Col xs={6}><span className="text-muted small">Fin</span><strong>{enchere.dateFin || "-"}</strong></Col>
                        </Row>
                        <div className="small text-muted mb-3">
                          Groupes: {(enchere.groupeNoms || []).join(", ") || "Selon ciblage"}<br />
                          Férailleurs: {(enchere.ferrailleurNoms || []).join(", ") || "Aucun ciblage nominatif"}
                        </div>
                        <div className="d-flex gap-2">
                          <Button size="sm" color="light" onClick={() => editEnchere(enchere)}>Modifier</Button>
                          <Button size="sm" color="light" className="text-danger" onClick={() => setDeleteTarget(enchere)}>Supprimer</Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Col>
        </Row>
      </Container>

      <Modal isOpen={Boolean(deleteTarget)} toggle={() => setDeleteTarget(null)} backdrop="static">
        <ModalHeader toggle={() => setDeleteTarget(null)}>Confirmation</ModalHeader>
        <ModalBody>Supprimer cette enchère ?</ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDeleteTarget(null)}>Annuler</Button>
          <Button color="danger" onClick={confirmDelete}>Supprimer</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default EncheresAdminPage;
